'use client';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FormData {
  name: string;
  phone: string;
  service: string;
  message: string;
}

const serviceOptions = [
  'Фундамент',
  'Монолитная плита',
  'Забор / подпорная стенка',
  'Другое',
];

export default function ContactSection() {
  const [form, setForm] = useState<FormData>({
    name: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const payload = Object.fromEntries(formData.entries());

    fetch('https://formspree.io/f/mzdyynwg', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) {
          setForm({ name: '', phone: '', service: '', message: '' });
          setSubmitted(true);
        } else {
          alert('Ошибка отправки. Попробуйте позже.');
        }
      })
      .catch((err) => {
        console.error('Formspree error:', err);
        alert('Не удалось отправить заявку. Проверьте интернет-соединение.');
      });
  };

  return (
    <section id="contact" ref={sectionRef} className="bg-background py-16 sm:py-24 relative overflow-hidden">
      <div
        className="absolute -right-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`reveal ${visible ? 'active' : ''} mb-10 sm:mb-16`}>
          <span className="text-xs font-mono uppercase text-muted-foreground tracking-widest mb-3 block">
            04 / Контакты
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-foreground">
            Оставить заявку
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mt-3 max-w-2xl">
            Заполните форму ниже, и наш менеджер свяжется с вами в течение 30 минут для обсуждения вашего проекта.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start">
          <div className={`reveal ${visible ? 'active' : ''} delay-100 lg:col-span-7`}>
            {submitted ? (
              <div className="bg-card border border-border rounded-2xl p-8 sm:p-12 text-center flex flex-col items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="CheckCircleIcon" size={40} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Заявка отправлена!</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                    Наш менеджер свяжется с вами в течение 30 минут. Работаем с 7:00 до 22:00 без выходных.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: '', phone: '', service: '', message: '' });
                  }}
                  className="text-sm font-medium text-primary border-b border-primary pb-0.5 hover:text-accent transition-colors"
                >
                  Отправить ещё одну заявку
                </button>
              </div>
            ) : (
              <form
                method="post"
                action="https://formspree.io/f/mzdyynwg"
                onSubmit={handleSubmit}
                className="bg-card border border-border rounded-2xl p-6 sm:p-8 md:p-10 space-y-4 sm:space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">
                      Ваше имя <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Иван Петров"
                      className="calc-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">
                      Телефон <span className="text-primary">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="+375 (XX) XXX-XX-XX"
                      className="calc-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Вид работ
                  </label>
                  <select name="service" value={form.service} onChange={handleChange} className="calc-input">
                    <option value="">Выберите услугу</option>
                    {serviceOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Комментарий
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Опишите объект, площадь, сроки..."
                    className="calc-input resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary text-white py-4 rounded-xl font-bold text-base hover:bg-accent transition-all duration-300 flex items-center justify-center gap-2 shadow-lg btn-pulse"
                >
                  Узнать стоимость
                  <Icon name="ArrowRightIcon" size={18} />
                </button>

                <p className="text-xs text-muted-foreground text-center">
                  Нажимая кнопку, вы соглашаетесь с обработкой персональных данных
                </p>
              </form>
            )}
          </div>

          <div className={`reveal ${visible ? 'active' : ''} delay-300 lg:col-span-5 flex flex-col gap-4`}>
            <div className="rounded-2xl overflow-hidden h-44 sm:h-48 relative">
              <AppImage
                src="https://images.unsplash.com/photo-1596586605821-cb6ed63a4de8"
                alt="SlavStroy construction team at work, sunny day, workers in orange safety vests, bright industrial setting"
                fill
                sizes="(max-width: 1024px) 100vw, 42vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="text-white font-bold text-sm">Команда СлавСтрой</span>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
              <h3 className="font-bold text-foreground text-base mb-4">Свяжитесь напрямую</h3>
              {[
                { icon: 'PhoneIcon', label: 'Телефон', value: '+375 (29) 604-70-61', href: 'tel:+375296047061' },
                { icon: 'ChatBubbleLeftRightIcon', label: 'Viber / WhatsApp', value: '+375 (29) 604-70-61', href: 'viber://chat?number=+375296047061' },
                { icon: 'MapPinIcon', label: 'Регион работы', value: 'Беларусь, Минск и Минская облась', href: '#' },
                { icon: 'ClockIcon', label: 'Режим работы', value: 'Пн–Вс, 9:00–18:00', href: '#' },
              ].map((item) => (
                <a key={item.label} href={item.href} className="flex items-center gap-3 group">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-200">
                    <Icon
                      name={item.icon as Parameters<typeof Icon>[0]['name']}
                      size={16}
                      className="text-primary group-hover:text-white transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="text-sm font-semibold text-foreground">{item.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}